import { useEffect, useState } from 'react'
import { useAdminApi } from '../api'
import { t, DictKey, categoriaLabel } from '../i18n'
import { useLang } from '../LangContext'
import { EquipoDetail } from './EquipoDetail'
import editStyles from '../adminEdit.module.css'
import { Icon } from '../../styles/Icon'

interface Equipo {
  id: string
  nombre: string
  categoria: string
  temporada_id: string
  color?: string | null
  icono?: string | null
  minutos_por_periodo: number
  num_periodos: number
  dias_entrenamiento: number[]
  orden: number
}

interface Temporada {
  id: string
  nombre: string
}

// Issue #54, 2026-10-02: "el panel de equipos ahora tiene tres botones separados: F11, F7 y UKE
// Escola... al volver atrás, regresa al menú principal, sin mostrar todos los equipos a la vez
// como antes".
const CATEGORIAS = ['f11', 'f7', 'eskola'] as const

const DIAS = [1, 2, 3, 4, 5, 6, 7]
const DIA_KEYS: Record<number, DictKey> = {
  1: 'lunes',
  2: 'martes',
  3: 'miercoles',
  4: 'jueves',
  5: 'viernes',
  6: 'sabado',
  7: 'domingo',
}

export function EquiposPage() {
  const api = useAdminApi()
  const lang = useLang()
  const [temporadas, setTemporadas] = useState<Temporada[]>([])
  const [temporadaId, setTemporadaId] = useState<string>('')
  const [equipos, setEquipos] = useState<Equipo[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [categoriaFiltro, setCategoriaFiltro] = useState<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const [form, setForm] = useState({
    nombre: '',
    categoria: 'f7',
    minutos_por_periodo: 25,
    num_periodos: 3,
    dias_entrenamiento: [] as number[],
  })
  const [iconoFile, setIconoFile] = useState<File | null>(null)

  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result))
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  useEffect(() => {
    api.get('/admin/temporadas').then((data: Temporada[]) => {
      setTemporadas(data)
      if (data[0]) setTemporadaId(data[0].id)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function refresh() {
    if (!temporadaId) return
    setEquipos(await api.get(`/admin/equipos?temporada_id=${temporadaId}`))
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporadaId])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!temporadaId) return
    const icono = iconoFile ? await readFileAsDataUrl(iconoFile) : undefined
    await api.post('/admin/equipos', {
      ...form,
      // Issue #42: UKE Eskola no tiene minutaje (asistencia por click, igual que F7); el backend
      // sigue exigiendo estos campos en la creación, así que se mandan a 0 en vez de mostrarlos.
      ...(form.categoria === 'eskola' ? { minutos_por_periodo: 0, num_periodos: 0 } : {}),
      icono,
      temporada_id: temporadaId,
      dias_entrenamiento: form.dias_entrenamiento,
    })
    setForm({
      nombre: '',
      categoria: 'f7',
      minutos_por_periodo: 25,
      num_periodos: 3,
      dias_entrenamiento: [],
    })
    setIconoFile(null)
    await refresh()
  }

  async function handleDelete(id: string, nombre: string) {
    if (!window.confirm(t('borrarEquipoConfirm', lang))) return
    try {
      await api.del(`/admin/equipos/${id}`)
      if (selected === id) setSelected(null)
      await refresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('error', lang)
      // Issue #30: si el bloqueo es por datos asociados, ofrecer el borrado forzado (con
      // confirmación reforzada: escribir el nombre exacto del equipo) en vez de solo informar.
      if (message.includes('tiene datos asociados')) {
        await handleForceDelete(id, nombre, message)
      } else {
        window.alert(message)
      }
    }
  }

  async function handleForceDelete(id: string, nombre: string, motivo: string) {
    if (!window.confirm(`${motivo}\n\n${t('forzarBorrarEquipoConfirm', lang)}`)) return
    const escrito = window.prompt(t('forzarBorrarEquipoEscribirNombre', lang).replace('{nombre}', nombre))
    if (escrito !== nombre) {
      if (escrito !== null) window.alert(t('forzarBorrarEquipoNoCoincide', lang))
      return
    }
    try {
      await api.del(`/admin/equipos/${id}?forzado=true`)
      if (selected === id) setSelected(null)
      await refresh()
    } catch (error) {
      window.alert(error instanceof Error ? error.message : t('error', lang))
    }
  }

  function toggleDia(dia: number) {
    setForm((f) => ({
      ...f,
      dias_entrenamiento: f.dias_entrenamiento.includes(dia)
        ? f.dias_entrenamiento.filter((d) => d !== dia)
        : [...f.dias_entrenamiento, dia],
    }))
  }

  // Issue #53, 2026-10-02: "permitir que el administrador cambie libremente el orden de los
  // equipos... y que esos cambios se actualicen automáticamente en todos los apartados donde
  // aparezcan esos equipos" — mismo mecanismo de arrastrar-y-soltar ya usado para jugadores.
  async function handleDropEquipo(targetId: string) {
    if (!dragId || dragId === targetId || !categoriaFiltro) return
    const equiposCategoria = equipos.filter((e) => e.categoria === categoriaFiltro).sort((a, b) => a.orden - b.orden)
    const fromIndex = equiposCategoria.findIndex((e) => e.id === dragId)
    const toIndex = equiposCategoria.findIndex((e) => e.id === targetId)
    if (fromIndex === -1 || toIndex === -1) return
    const reordenados = [...equiposCategoria]
    const [moved] = reordenados.splice(fromIndex, 1)
    reordenados.splice(toIndex, 0, moved)

    await Promise.all(reordenados.map((e, index) => api.patch(`/admin/equipos/${e.id}`, { orden: index })))
    setDragId(null)
    await refresh()
  }

  return (
    <div>
      <h2>{t('equipos', lang)}</h2>
      <select value={temporadaId} onChange={(e) => setTemporadaId(e.target.value)}>
        {temporadas.map((temporada) => (
          <option key={temporada.id} value={temporada.id}>
            {temporada.nombre}
          </option>
        ))}
      </select>

      {/* fix-mobile-responsive: cada campo con su etiqueta visible (antes "25" y "3" sin contexto) y
          los días agrupados como en el formulario de edición. */}
      <form onSubmit={handleCreate} className={editStyles.createForm}>
        <label>
          {t('nombre', lang)}
          <input
            placeholder={t('nombre', lang)}
            required
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
          />
        </label>
        <label>
          {t('categoria', lang)}
          <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
            <option value="eskola">{categoriaLabel('eskola', lang)}</option>
            <option value="f7">{categoriaLabel('f7', lang)}</option>
            <option value="f11">{categoriaLabel('f11', lang)}</option>
          </select>
        </label>
        <label>
          {t('icono', lang)}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setIconoFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {form.categoria !== 'eskola' && (
          <>
            <label>
              {t('minutosPorPeriodo', lang)}
              <input
                type="number"
                value={form.minutos_por_periodo}
                onChange={(e) => setForm({ ...form, minutos_por_periodo: Number(e.target.value) })}
              />
            </label>
            <label>
              {t('numPeriodos', lang)}
              <input
                type="number"
                value={form.num_periodos}
                onChange={(e) => setForm({ ...form, num_periodos: Number(e.target.value) })}
              />
            </label>
          </>
        )}
        <fieldset className={editStyles.dias}>
          <legend>{t('diasEntrenamiento', lang)}</legend>
          {DIAS.map((dia) => (
            <label key={dia}>
              <input
                type="checkbox"
                checked={form.dias_entrenamiento.includes(dia)}
                onChange={() => toggleDia(dia)}
              />
              {t(DIA_KEYS[dia], lang)}
            </label>
          ))}
        </fieldset>
        <button type="submit"><Icon name="crear" />{t('crear', lang)}</button>
      </form>

      {!categoriaFiltro ? (
        <div>
          {CATEGORIAS.map((cat) => (
            <button key={cat} onClick={() => setCategoriaFiltro(cat)}>
              {categoriaLabel(cat, lang)}
            </button>
          ))}
        </div>
      ) : (
        <>
          <button
            onClick={() => {
              setCategoriaFiltro(null)
              setSelected(null)
            }}
          >
            <Icon name="volver" />
            {t('volver', lang)}
          </button>
          <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('nombre', lang)}</th>
                <th>{t('categoria', lang)}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {equipos
                .filter((equipo) => equipo.categoria === categoriaFiltro)
                .sort((a, b) => a.orden - b.orden)
                .map((equipo) => (
                  <tr
                    key={equipo.id}
                    draggable
                    onDragStart={() => setDragId(equipo.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDropEquipo(equipo.id)}
                    aria-current={selected === equipo.id ? 'true' : undefined}
                    className={selected === equipo.id ? editStyles.rowEditing : undefined}
                  >
                    <td>{equipo.nombre}</td>
                    <td>{categoriaLabel(equipo.categoria, lang)}</td>
                    <td>
                      <button onClick={() => setSelected(equipo.id)}><Icon name="editar" />{t('editar', lang)}</button>
                      <button onClick={() => handleDelete(equipo.id, equipo.nombre)}><Icon name="eliminar" />{t('eliminar', lang)}</button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      {selected && equipos.find((equipo) => equipo.id === selected) && (
        <EquipoDetail
          equipo={equipos.find((equipo) => equipo.id === selected)!}
          onClose={() => setSelected(null)}
          onUpdated={refresh}
        />
      )}
    </div>
  )
}
